/**
 * Product Image Carousel Module
 * Handles swipe, drag, click, and keyboard navigation for product image galleries
 */

class T4sProductCarousel {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      maxImages: 5,
      enableDots: true,
      enableArrows: true,
      autoPlay: 'disabled',
      transitionSpeed: 300,
      enableSwipe: true,
      enableKeyboard: true,
      enableTouchHint: true,
      cycleImages: true,
      ...options
    };

    this.currentIndex = 0;
    this.isTransitioning = false;
    this.autoPlayTimeout = null;
    this.isDragging = false;
    this.dragMoved = false;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.dragOffsetX = 0;
    this.touchHintShown = false;
    this.dragStartTime = 0;

    this.init();
  }

  init() {
    this.wrapper = this.container.querySelector('.t4s-carousel-wrapper');
    this.slides = this.container.querySelector('.t4s-carousel-slides');
    this.allSlides = this.container.querySelectorAll('.t4s-carousel-slide');

    // Dots can be inside container or outside (with --below modifier)
    let dotsContainer = this.container.querySelector('.t4s-carousel-dots');
    if (!dotsContainer) {
      const parentEl = this.container.closest('[data-product-options]');
      const parentId = parentEl && parentEl.dataset.productOptions ? ((parentEl.dataset.productOptions.match(/"id":"(\d+)"/) || [])[1]) : null;
      if (parentId) {
        dotsContainer = document.querySelector('.t4s-carousel-dots--below[data-carousel-parent="' + parentId + '"]');
      }
      if (!dotsContainer && this.container.parentNode) {
        dotsContainer = this.container.parentNode.querySelector('.t4s-carousel-dots--below');
      }
    }
    this.dotsContainer = dotsContainer;

    this.prevBtn = this.container.querySelector('.t4s-carousel-nav.prev');
    this.nextBtn = this.container.querySelector('.t4s-carousel-nav.next');
    this.counter = this.container.querySelector('.t4s-carousel-counter');

    if (!this.wrapper || this.allSlides.length === 0) {
      console.warn('Carousel initialization failed: missing wrapper or slides');
      return;
    }

    this.setupEventListeners();
    this.updateCarousel();
    this.setupAutoPlay();
  }

  setupEventListeners() {
    const self = this;

    // Single delegated handler for arrows, dots and product navigation.
    // One listener keeps bindings reliable even if the DOM is re-rendered.
    this.container.addEventListener('click', function(e) {
      const target = e.target && e.target.closest ? e.target.closest('.t4s-carousel-nav, .t4s-carousel-dot') : null;

      if (target) {
        e.preventDefault();
        e.stopPropagation();

        // Arrows/dots must win over any lingering drag-transition lock.
        self.isTransitioning = false;

        if (target.classList.contains('t4s-carousel-nav')) {
          if (target.classList.contains('prev')) {
            self.prev();
          } else {
            self.next();
          }
        } else {
          const dots = self.dotsContainer ? self.dotsContainer.querySelectorAll('.t4s-carousel-dot') : [];
          const idx = Array.prototype.indexOf.call(dots, target);
          if (idx > -1) {
            self.goToSlide(idx);
          }
        }
        return;
      }

      // Plain click on the image navigates to the product (suppressed after a drag)
      if (self.dragMoved) {
        e.preventDefault();
        e.stopPropagation();
        self.dragMoved = false;
        return;
      }
      self.navigateToProduct();
    });

    // Swipe and drag
    if (this.options.enableSwipe) {
      // Touch events
      this.wrapper.addEventListener('touchstart', function(e) {
        self.handleDragStart(e);
      }, { passive: true });

      this.wrapper.addEventListener('touchmove', function(e) {
        self.handleDragMove(e);
      }, { passive: false });

      this.wrapper.addEventListener('touchend', function(e) {
        self.handleDragEnd(e);
      }, { passive: true });

      // Mouse events
      this.wrapper.addEventListener('mousedown', function(e) {
        self.handleDragStart(e);
      });

      this.wrapper.addEventListener('mousemove', function(e) {
        self.handleDragMove(e);
      });

      this.wrapper.addEventListener('mouseup', function(e) {
        self.handleDragEnd(e);
      });

      this.wrapper.addEventListener('mouseleave', function(e) {
        self.handleDragEnd(e);
      });
    }

    // Auto-play on hover
    if (this.options.autoPlay === 'hover') {
      this.container.addEventListener('mouseenter', () => this.startAutoPlay());
      this.container.addEventListener('mouseleave', () => this.stopAutoPlay());
    }

    // Touch hint on first interaction
    if (this.options.enableTouchHint && this.options.enableSwipe) {
      this.wrapper.addEventListener('touchstart', () => this.hideTouchHint(), { once: true });
    }
  }

  handleDragStart(e) {
    if (this.isTransitioning) return;

    this.isDragging = true;
    this.dragMoved = false;
    this.dragStartTime = Date.now();
    this.dragStartX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    this.dragStartY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;
    this.dragOffsetX = 0;

    // Temporarily disable transitions for smooth dragging
    this.slides.style.transition = 'none';
    this.slides.style.willChange = 'transform';
  }

  handleDragMove(e) {
    if (!this.isDragging) return;

    const currentX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    const currentY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;

    this.dragOffsetX = currentX - this.dragStartX;
    const dragOffsetY = currentY - this.dragStartY;

    if (Math.abs(this.dragOffsetX) > 5) {
      this.dragMoved = true;
    }

    // Only drag horizontally if horizontal movement is greater than vertical
    if (Math.abs(this.dragOffsetX) > Math.abs(dragOffsetY)) {
      e.preventDefault();

      const currentTranslate = -this.currentIndex * 100;
      const dragPercent = (this.dragOffsetX / this.wrapper.offsetWidth) * 100;
      this.slides.style.transform = `translateX(${currentTranslate + dragPercent}%)`;
    }
  }

  handleDragEnd(e) {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.slides.style.transition = `transform ${this.options.transitionSpeed}ms ease-in-out`;
    this.slides.style.willChange = 'auto';

    // A pure click (no movement) must not lock the carousel into a transition,
    // otherwise the browser's subsequent 'click' event on arrows/dots is swallowed.
    if (this.dragOffsetX === 0) {
      return;
    }

    const dragThreshold = this.wrapper.offsetWidth * 0.15; // 15% threshold
    const dragTime = Date.now() - this.dragStartTime;
    const isQuickFlick = dragTime < 200 && Math.abs(this.dragOffsetX) > 30;

    if (isQuickFlick || Math.abs(this.dragOffsetX) > dragThreshold) {
      if (this.dragOffsetX > 0) {
        this.prev();
      } else {
        this.next();
      }
    } else {
      this.updateCarousel();
    }
  }

  prev() {
    if (this.isTransitioning) return;

    if (this.currentIndex > 0) {
      this.currentIndex--;
    } else if (this.options.cycleImages) {
      this.currentIndex = this.allSlides.length - 1;
    }

    this.updateCarousel();
  }

  next() {
    if (this.isTransitioning) return;

    if (this.currentIndex < this.allSlides.length - 1) {
      this.currentIndex++;
    } else if (this.options.cycleImages) {
      this.currentIndex = 0;
    }

    this.updateCarousel();
  }

  goToSlide(index) {
    if (this.isTransitioning || index === this.currentIndex) return;

    if (index >= 0 && index < this.allSlides.length) {
      this.currentIndex = index;
      this.updateCarousel();
    }
  }

  updateCarousel() {
    // Set CSS variable for transition speed
    this.container.style.setProperty('--carousel-speed', `${this.options.transitionSpeed}ms`);

    // Update slide position
    const offset = -this.currentIndex * 100;
    this.slides.style.transform = `translateX(${offset}%)`;

    // Mark transition as active
    this.isTransitioning = true;
    setTimeout(() => {
      this.isTransitioning = false;
    }, this.options.transitionSpeed);

    // Update active states on slides
    this.allSlides.forEach((slide, index) => {
      slide.classList.toggle('is--active', index === this.currentIndex);
    });

    // Update dots (both internal and external)
    const allDotsContainers = [
      this.dotsContainer,
      ...document.querySelectorAll('.t4s-carousel-dots--below')
    ].filter(Boolean);

    allDotsContainers.forEach(dotsContainer => {
      // Only update dots that belong to this carousel
      const dots = dotsContainer.querySelectorAll('.t4s-carousel-dot');
      dots.forEach((dot, index) => {
        if (index < this.allSlides.length) {
          dot.classList.toggle('is--active', index === this.currentIndex);
        }
      });
    });

    // Update counter
    if (this.counter) {
      const currentEl = this.counter.querySelector('.current');
      if (currentEl) {
        currentEl.textContent = this.currentIndex + 1;
      }
    }

    // Update arrow button states
    this.updateArrowStates();

    // Trigger custom event
    const event = new CustomEvent('carouselChanged', {
      detail: { currentIndex: this.currentIndex, totalSlides: this.allSlides.length }
    });
    this.container.dispatchEvent(event);
  }

  updateArrowStates() {
    if (!this.prevBtn || !this.nextBtn) return;

    const isAtStart = this.currentIndex === 0;
    const isAtEnd = this.currentIndex === this.allSlides.length - 1;

    if (!this.options.cycleImages) {
      this.prevBtn.classList.toggle('is--disabled', isAtStart);
      this.nextBtn.classList.toggle('is--disabled', isAtEnd);
      this.prevBtn.disabled = isAtStart;
      this.nextBtn.disabled = isAtEnd;
    } else {
      this.prevBtn.classList.remove('is--disabled');
      this.nextBtn.classList.remove('is--disabled');
      this.prevBtn.disabled = false;
      this.nextBtn.disabled = false;
    }
  }

  setupAutoPlay() {
    if (this.options.autoPlay === 'always') {
      this.startAutoPlay();
    }
  }

  startAutoPlay() {
    if (this.autoPlayTimeout) return;

    const interval = Math.max(3000, this.options.transitionSpeed + 2700);
    this.autoPlayTimeout = setInterval(() => {
      if (this.currentIndex < this.allSlides.length - 1) {
        this.next();
      } else if (this.options.cycleImages) {
        this.currentIndex = 0;
        this.updateCarousel();
      }
    }, interval);
  }

  stopAutoPlay() {
    if (this.autoPlayTimeout) {
      clearInterval(this.autoPlayTimeout);
      this.autoPlayTimeout = null;
    }
  }

  navigateToProduct() {
    const productEl = this.container.closest('.t4s-product');
    const link = productEl ? productEl.querySelector('.t4s-full-width-link') : null;
    const href = link ? link.getAttribute('href') : null;
    if (href) {
      window.location.href = href;
    }
  }

  hideTouchHint() {
    const hint = this.container.querySelector('.t4s-carousel-touch-hint');
    if (hint && !this.touchHintShown) {
      hint.style.display = 'none';
      this.touchHintShown = true;
    }
  }

  destroy() {
    this.stopAutoPlay();
  }
}

// Initialize all carousels on page
function initProductCarousels() {
  const carousels = document.querySelectorAll('.t4s-product-carousel');

  carousels.forEach((carouselContainer) => {
    // Check if already initialized
    if (carouselContainer.dataset.carouselInitialized) {
      return;
    }

    // Get settings from data attributes with proper type conversion
    const enableDots = carouselContainer.dataset.enableDots !== 'false';
    const enableArrows = carouselContainer.dataset.enableArrows !== 'false';
    const autoPlay = carouselContainer.dataset.autoPlay || 'disabled';

    const settings = {
      maxImages: parseInt(carouselContainer.dataset.maxImages) || 5,
      enableDots: enableDots,
      enableArrows: enableArrows,
      autoPlay: autoPlay,
      transitionSpeed: parseInt(carouselContainer.dataset.transitionSpeed) || 300,
      enableSwipe: carouselContainer.dataset.enableSwipe !== 'false',
      cycleImages: carouselContainer.dataset.cycleImages !== 'false'
    };

    new T4sProductCarousel(carouselContainer, settings);
    carouselContainer.dataset.carouselInitialized = 'true';
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductCarousels);
} else {
  setTimeout(initProductCarousels, 50);
}

// Also try on window load
window.addEventListener('load', function() {
  setTimeout(initProductCarousels, 100);
});

  // Use MutationObserver to detect newly added carousels (for AJAX-loaded content)
  const carouselObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        // Check if the added node itself is a carousel
        if (node.matches && node.matches('.t4s-product-carousel')) {
          if (!node.dataset.carouselInitialized) {
            initProductCarousels(); // re-run initialization (will skip already initialized)
            break; // avoid multiple triggers
          }
        }
        // Also check for descendants that are carousels
        const carouselsInNode = node.querySelectorAll ? node.querySelectorAll('.t4s-product-carousel') : [];
        carouselsInNode.forEach(c => {
          if (!c.dataset.carouselInitialized) {
            initProductCarousels();
          }
        });
      }
    }
  });

  // Start observing the document body for added nodes
  carouselObserver.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Re-initialize for dynamically added products (fallback)
  document.addEventListener('ntajax:updated', function() {
    initProductCarousels();
  });

  window.addEventListener('products:updated', function() {
    initProductCarousels();
  });